import React, { FormEvent, useEffect, useState } from 'react'
import Router, { useRouter } from 'next/router'
import { Trans, t } from '@lingui/macro'
import ReactGA from 'react-ga'
import { observer } from 'mobx-react-lite'
import { ButtonLayout, ButtonPrimary, Input, NO_MESSAGE_ERROR } from '../../ui-kit'

import { Auth } from './Auth'
import { IS_DEMO } from '../../../config'
import { LINKS } from '../../../config/constants'
import { ModalForgottenPassword } from './ModalForgottenPassword'
import { SignInForm } from './SignInForm'
import { _ } from '../../utils/intl'
import { appendRedirectParamsFromRouter } from '../../utils/appendRedirectParamsFromRouter'
import { extractMessageFromAxiosError } from '../../utils/extractMessageFromAxiosError'
import { fieldToInputProps } from '../../utils/form/fieldToInputProps'
import { getURLSearchParamNoArray } from '../../utils/routing'
import { useDialog } from '../../utils/hooks/useDialog'

export const SignIn = observer(() => {
  const router = useRouter()
  const [form] = useState(() => new SignInForm())
  const [attempting, setAttempting] = useState(false)
  const d = useDialog()
  const showForgottenPassword = getURLSearchParamNoArray(router.query.showForgottenPassword)

  useEffect(() => {
    if (showForgottenPassword != null) {
      d.open()
    }
  }, [d, showForgottenPassword])

  useEffect(() => {
    // monkey patch for chrome password autofill
    const browserFilledEmail = (document.getElementById('email') as HTMLInputElement).value
    const browserFilledPassword = (document.getElementById('password') as HTMLInputElement).value

    if (browserFilledEmail.length) {
      form.fields.email.onChange(browserFilledEmail)
    }

    if (browserFilledPassword.length) {
      form.fields.password.onChange(browserFilledPassword)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const { hasError } = await form.form.validate()
    if (hasError) return console.warn(form.form.error)

    setAttempting(true)

    const body = form.serialize()

    try {
      await Auth.login(body.email, body.password)

      ReactGA.event({
        category: 'Auth',
        action: 'Login',
      })

      const redirect = appendRedirectParamsFromRouter(router)
      if (redirect) {
        Router.push(decodeURIComponent(redirect))
      } else if (location.pathname.includes('/auth')) {
        location.pathname = '/'
      } else {
        location.reload()
      }
    } catch (err) {
      form.fields.email.setError(NO_MESSAGE_ERROR)
      form.fields.password.setError(extractMessageFromAxiosError(err))
    } finally {
      setAttempting(false)
    }
  }

  const { email, password } = form.fields

  return (
    <>
      <form onSubmit={submit} noValidate>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          label={<Trans>EMAIL, TEL. ČÍSLO ALEBO ČÍSLO KARTY</Trans>}
          labelDescription={_(
            t`Email alebo tel. číslo musia byť zaregistrované vo vašom profile v Zóne Up`
          )}
          isRequired
          style={{ lineHeight: 'inherit' }} // fixed browser autofill issue (bottom cropping of letter 'g')
          {...fieldToInputProps(email, (val: string) => val.trim())}
        />
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          withPasswordToggler
          label={<Trans>Prihlasovacie heslo</Trans>}
          isRequired
          {...fieldToInputProps(password, (val: string) => val.trim())}
        />
        <p className="mb-xxlarge">
          <a onClick={d.open} className="link-black">
            <Trans>Neviem heslo</Trans>
          </a>
        </p>

        {IS_DEMO ? (
          <div
            style={{
              background: '#ff0000',
              color: '#ffffff',
              padding: '14px 10px',
              marginBottom: 10,
              display: 'flex',
              justifyContent: 'center',
              borderRadius: 4,
            }}>
            POZOR - testovacie prostredie
          </div>
        ) : null}

        <ButtonLayout className="mb-large">
          <ButtonPrimary size="large" submit isDisabled={attempting}>
            <Trans>Prihlásiť sa</Trans>
          </ButtonPrimary>
        </ButtonLayout>
        <p className="-mt-2">
          <a href={LINKS.gdpr} target="_blank" className="link-black">
            <Trans>Zásady spracúvania osobných údajov</Trans>
          </a>
        </p>
      </form>
      {d.isOpen && <ModalForgottenPassword close={d.close} />}
    </>
  )
})
